// src/pages/GoldSchemeList.jsx
import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaFilter,
  FaTimesCircle,
} from "react-icons/fa";
import { TbArrowsSort } from "react-icons/tb";
import { FiRotateCcw } from "react-icons/fi";
import Select from "react-select";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

import ConfirmDialog from "../components/ConfirmDialog";
import { handleDropDowns } from "../config/dropDownHelpers";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import {
  useGetAllGoldSchemesQuery,
  useToggleGoldSchemeActiveMutation,
  useDeleteGoldSchemeMutation,
} from "../toolkitServices/goldSchemeApi";

/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

/* ------------ react-select styles (compact) ------------ */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 8,
    borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 1px #7c3aed33" : "none",
    "&:hover": { borderColor: "#7c3aed" },
    fontSize: "0.8rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#111827",
  }),
  placeholder: (base) => ({ ...base, color: "#9ca3af", fontSize: "0.8rem" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, color: "#111827" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  option: (base, state) => ({
    ...base,
    fontSize: "0.8rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    backgroundColor: state.isFocused
      ? "#ede9fe"
      : state.isSelected
        ? "#ddd6fe"
        : "white",
    color: "#111827",
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const paginationSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    borderRadius: 6,
    borderColor: state.isFocused ? "#9481ce" : "#e2e8f0",
    boxShadow: state.isFocused
      ? "0 0 0 1px rgba(148,129,206,.25)"
      : "none",
    fontSize: 10,
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: "0 5px" }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  indicatorSeparator: () => ({ display: "none" }),
  menu: (base) => ({ ...base, zIndex: 70, fontSize: 10 }),
  menuList: (base) => ({ ...base, maxHeight: 160, padding: 2 }),
  option: (base, state) => ({
    ...base,
    padding: "6px 8px",
    backgroundColor: state.isSelected
      ? "#9481ce"
      : state.isFocused
        ? "#ede9fe"
        : "white",
    color: state.isSelected ? "white" : "#334155",
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const headerCell =
  "px-3 sm:px-4 py-1.5 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide";
const dataCell = "px-3 sm:px-4 py-1.5 text-xs sm:text-sm";

/* ------------ small helpers ------------ */
const toTitle = (s = "") =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

/* ------------ skeleton rows ------------ */
const SkeletonRows = ({ cols = 10, rows = 6 }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <tr key={`sk-${r}`} className="animate-pulse">
        {Array.from({ length: cols }).map((__, c) => (
          <td key={`sk-${r}-${c}`} className={dataCell}>
            <div className="h-3 w-20 bg-slate-200 rounded" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

const canDelete = (user) => {
  const perm = user?.permissions?.find((p) => p.moduleKey === "quotation");
  return user?.role === "owner" || perm?.permissions?.delete;
};

const GoldSchemeList = () => {
  const navigate = useNavigate();
  const authUser = useSelector((s) => s.auth?.user);
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  /* ------------ table state ------------ */
  const [search, setSearch] = useState("");
  const [schemeTypeFilter, setSchemeTypeFilter] = useState(""); // AMOUNT / GRAM / ""
  const [statusFilter, setStatusFilter] = useState("true"); // "true" | "false" | ""
  const [branchFilter, setBranchFilter] = useState(authUser?.branchId || "");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" | "desc"

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [confirm, setConfirm] = useState({
    open: false,
    mode: null, // 'delete'
    payload: null, // { id, name }
  });

  /* ------------ focus mgmt for delete confirm ------------ */
  const lastDeleteBtnRef = useRef(null);
  const skipRestoreFocusRef = useRef(false);
  const [focusAfterDeleteId, setFocusAfterDeleteId] = useState(null);

  /* ------------ branches for filter ------------ */
  const { data: branchesRes } = useGetAllBranchesQuery();
  const branchOptions = useMemo(
    () =>
      handleDropDowns({
        key: "branch",
        branches: branchesRes?.data || branchesRes?.items || [],
      }),
    [branchesRes],
  );

  const branchValue = useMemo(
    () =>
      branchOptions.find(
        (o) => String(o.value) === String(branchFilter || authUser?.branchId),
      ) || null,
    [branchOptions, branchFilter, authUser?.branchId],
  );

  /* ------------ query: Gold schemes ------------ */
  const { data, isFetching, isSuccess } = useGetAllGoldSchemesQuery({
    page: 1,
    limit: 1000,
    search,
    schemeType: schemeTypeFilter || undefined,
    isActive:
      statusFilter === "" ? undefined : statusFilter === "true" ? true : false,
    branchId: branchFilter || authUser?.branchId,
  });

  const schemes = useMemo(
    () => (Array.isArray(data?.data) ? data.data : []),
    [data],
  );

  // local sort (since backend sort nahi kar raha)
  const sortedSchemes = useMemo(() => {
    const copy = [...schemes];

    const getVal = (s) => {
      switch (sortBy) {
        case "schemeName":
          return (s.schemeName || "").toLowerCase();
        case "schemeType":
          return (s.schemeType || "").toLowerCase();
        case "installmentMonths":
          return Number(s.installmentMonths) || 0;
        case "schemeAmountPerMonth":
          return Number(s.schemeAmountPerMonth) || 0;
        case "createdAt":
        default:
          return s.createdAt ? new Date(s.createdAt).getTime() : 0;
      }
    };

    copy.sort((a, b) => {
      const av = getVal(a);
      const bv = getVal(b);
      if (av < bv) return sortOrder === "asc" ? -1 : 1;
      if (av > bv) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return copy;
  }, [schemes, sortBy, sortOrder]);

  // pagination on FE
  const total = sortedSchemes.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const rows = sortedSchemes.slice(startIndex, endIndex);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        navigate("/scheme/gold-scheme/create");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  useEffect(() => {
    setPage(1);
  }, [search, schemeTypeFilter, statusFilter, branchFilter, limit]);

  /* ------------ RTK mutations ------------ */
  const [toggleActive, { isLoading: toggling }] =
    useToggleGoldSchemeActiveMutation();
  const [deleteScheme, { isLoading: deleting }] = useDeleteGoldSchemeMutation();

  /* ------------ helpers ------------ */
  const onSort = (field) => {
    if (sortBy === field) {
      setSortOrder((s) => (s === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const sortIcon = (field) => (
    <span
      className="inline-flex items-center gap-1 cursor-pointer select-none"
      onClick={() => onSort(field)}
      title="Sort"
    >
      <TbArrowsSort className="text-[16px]" />
      {sortBy === field ? (
        <span className="text-[10px]">{sortOrder.toUpperCase()}</span>
      ) : null}
    </span>
  );

  const openDeleteConfirm = (scheme, e) => {
    lastDeleteBtnRef.current = e?.currentTarget || null;
    skipRestoreFocusRef.current = false;

    setConfirm({
      open: true,
      mode: "delete",
      payload: { id: scheme._id, name: scheme.schemeName },
    });
  };

  const closeConfirm = useCallback(() => {
    setConfirm({ open: false, mode: null, payload: null });

    if (skipRestoreFocusRef.current) return;

    setTimeout(() => {
      try {
        lastDeleteBtnRef.current?.focus?.({ preventScroll: true });
      } catch {
        lastDeleteBtnRef.current?.focus?.();
      }
    }, 0);
  }, []);

  const handleConfirm = async () => {
    if (confirm.mode !== "delete" || !confirm.payload?.id) return;

    const deletedId = String(confirm.payload.id);

    const idx = sortedSchemes.findIndex((s) => String(s._id) === deletedId);
    const upper = idx > 0 ? sortedSchemes[idx - 1] : null;
    const fallback = idx >= 0 ? sortedSchemes[idx + 1] : null;
    const focusTargetId = upper?._id || fallback?._id || null;

    skipRestoreFocusRef.current = true;

    try {
      await deleteScheme({
        id: confirm.payload.id,
        branchId: branchFilter || authUser?.branchId,
      }).unwrap();

      toast.success("Scheme deleted successfully");

      if (focusTargetId) setFocusAfterDeleteId(String(focusTargetId));
    } catch (err) {
      const msg =
        err?.data?.message || err?.data?.error || "Failed to delete scheme";
      toast.error(msg);

      skipRestoreFocusRef.current = false;
    } finally {
      setConfirm({ open: false, mode: null, payload: null });

      if (!skipRestoreFocusRef.current) {
        setTimeout(() => {
          try {
            lastDeleteBtnRef.current?.focus?.({ preventScroll: true });
          } catch {
            lastDeleteBtnRef.current?.focus?.();
          }
        }, 0);
      }
    }
  };

  useEffect(() => {
    if (!focusAfterDeleteId) return;

    const tryFocus = () => {
      const el = document.querySelector(
        `[data-delete-btn="${focusAfterDeleteId}"]`,
      );
      if (el) {
        try {
          el.focus({ preventScroll: true });
        } catch {
          el.focus();
        }
        setFocusAfterDeleteId(null);
        return true;
      }
      return false;
    };

    if (tryFocus()) return;

    let tries = 0;
    const t = setInterval(() => {
      tries += 1;
      if (tryFocus() || tries >= 10) clearInterval(t);
    }, 60);

    return () => clearInterval(t);
  }, [focusAfterDeleteId, rows, sortedSchemes]);

  const handleToggleActive = async (scheme) => {
    try {
      await toggleActive({
        id: scheme._id,
        branchId: branchFilter || authUser?.branchId,
      }).unwrap();
      toast.success(
        `Scheme ${scheme.isActive ? "deactivated" : "activated"} successfully`,
      );
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.data?.error ||
        "Failed to update scheme status";
      toast.error(msg);
    }
  };

  /* ------------ applied filter chips ------------ */
  const chips = useMemo(() => {
    const arr = [];
    if (search) arr.push({ key: "search", label: `Search: ${search}` });
    if (schemeTypeFilter)
      arr.push({
        key: "schemeType",
        label: `Type: ${toTitle(schemeTypeFilter)}`,
      });
    if (statusFilter !== "")
      arr.push({
        key: "status",
        label: `Status: ${statusFilter === "true" ? "Active" : "Inactive"}`,
      });
    if (branchValue?.label)
      arr.push({ key: "branch", label: `Branch: ${branchValue.label}` });
    return arr;
  }, [search, schemeTypeFilter, statusFilter, branchValue]);

  const clearChip = (key) => {
    if (key === "search") setSearch("");
    else if (key === "schemeType") setSchemeTypeFilter("");
    else if (key === "status") setStatusFilter("");
    else if (key === "branch") setBranchFilter("");
  };

  const clearAllChips = () => {
    setSearch("");
    setSchemeTypeFilter("");
    setStatusFilter("");
    setBranchFilter(authUser?.branchId || "");
  };

  /* ------------ filter modal ------------ */
  const resetFilters = () => {
    setSchemeTypeFilter("");
    setStatusFilter("true");
    setBranchFilter(authUser?.branchId || "");
    setSearch("");
  };

  return (
    <div className="relative bg-white shadow-lg rounded-2xl flex flex-col h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {/* global loader */}
      {isFetching && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/50 pointer-events-none">
          <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
            <Spinner size={26} />
            <span>Loading gold schemes…</span>
          </div>
        </div>
      )}

      {/* header bar (slightly tighter on mobile) */}
      <div className="flex items-center justify-between px-4 py-2 rounded-t-xl bg-[#9481ce] text-white">
        <div className="flex items-center gap-2">
          <h2 className="text-base sm:text-lg font-semibold tracking-tight">
            Saving Schemes
          </h2>
          {isFetching && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10">
              <Spinner size={10} /> refreshing…
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="hidden sm:inline text-[11px] text-violet-100/90">
            Ctrl + P to create new
          </span>
          <Link
            to="/scheme/gold-scheme/create"
            className="inline-flex items-center gap-1 rounded-full bg-white text-[#6b4fd8] px-3 sm:px-4 py-1.5 text-[11px] sm:text-xs font-semibold shadow-sm hover:bg-violet-50 transition"
          >
            + Add New
          </Link>
        </div>
      </div>

      {/* top filters bar
          ✅ Mobile: only Search + Filters (more table height)
          ✅ Desktop: full quick filters
      */}
      <div className="px-3 sm:px-4 py-1.5 sm:py-2 border-b bg-slate-50/80">
        {/* MOBILE quick row */}
        <div className="flex items-center gap-2 sm:hidden">
          <input
            type="text"
            placeholder="Search schemes…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-violet-400"
          />
          <button
            onClick={() => setIsFilterOpen(true)}
            className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-violet-200 bg-white text-[#6b59c6] outline-[#6b59c6] hover:bg-violet-50"
            title="Advanced filters"
            aria-label="Advanced filters"
          >
            <FaFilter />
          </button>
        </div>

        {/* DESKTOP quick filters (same as your old) */}
        <div className="hidden sm:flex flex-wrap items-end gap-3">
          {/* Scheme type */}
          <div className="flex flex-col gap-[1px] w-[140px]">
            <label className="text-[11px] font-medium text-slate-600">
              Scheme Type
            </label>
            <select
              value={schemeTypeFilter}
              onChange={(e) => setSchemeTypeFilter(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            >
              <option value="">All</option>
              <option value="AMOUNT">Amount</option>
              <option value="GRAM">Gram</option>
            </select>
          </div>

          {/* Status */}
          <div className="flex flex-col gap-[1px] w-[140px]">
            <label className="text-[11px] font-medium text-slate-600">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
              <option value="">All</option>
            </select>
          </div>

          {/* Branch */}
          <div className="flex flex-col gap-[1px] w-[190px]">
            <label className="text-[11px] font-medium text-slate-600">
              Branch
            </label>
            <Select
              styles={selectStyles}
              value={branchValue}
              options={branchOptions}
              isClearable
              onChange={(opt) => setBranchFilter(opt?.value || "")}
              menuPortalTarget={document.body}
              menuPlacement="auto"
            />
          </div>

          {/* Search */}
          <div className="flex flex-col gap-[1px] flex-1 min-w-[160px]">
            <label className="text-[11px] font-medium text-slate-600">
              Search
            </label>
            <input
              type="text"
              placeholder="Scheme name/code/description…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-[11px] text-gray-800 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-violet-400"
            />
          </div>

          {/* Filter button */}
          <div className="flex flex-col gap-[1px] w-auto">
            <label className="text-[11px] font-medium text-slate-600 invisible">
              Filters
            </label>
            <button
              onClick={() => setIsFilterOpen(true)}
              className="inline-flex items-center gap-1 bg-white text-[#6b59c6] px-3 py-1.5 rounded-full text-[11px] border border-violet-200 hover:bg-violet-50 outline-[#6b59c6]"
              title="Advanced filters"
            >
              <FaFilter /> Filters
            </button>
          </div>
        </div>
      </div>

      {/* active chips (mobile: horizontal scroll to avoid height) */}
      {chips.length > 0 && (
        <div className="px-3 sm:px-4 py-1.5 border-b bg-slate-50 flex items-center gap-2">
          <div className="flex items-center gap-2 flex-nowrap overflow-x-auto min-w-0 pr-2">
            {chips.map((c) => (
              <span
                key={c.key}
                className="inline-flex items-center gap-2 text-[11px] bg-white border px-2 py-1 rounded-full shadow-sm whitespace-nowrap flex-shrink-0"
              >
                {c.label}
                <button
                  className="text-slate-500 hover:text-red-500"
                  onClick={() => clearChip(c.key)}
                  title="Clear"
                >
                  <FaTimesCircle />
                </button>
              </span>
            ))}
          </div>

          <button
            className="ml-auto text-[11px] text-[#6b59c6] hover:underline flex-shrink-0"
            onClick={clearAllChips}
            title="Clear all"
          >
            Clear all
          </button>
        </div>
      )}

      {/* table (✅ flex-1 so it gets maximum remaining height) */}
      <div className="relative min-h-0 flex-1 overflow-x-auto overflow-y-auto">
        <table className="min-w-full text-xs sm:text-sm text-left">
          <thead className="sticky top-0 z-40 bg-[#e6e1f5] text-[10px] uppercase text-[#4b3d91] sm:text-[11px]">
            <tr>
              <th className={headerCell}>#</th>
              <th className={headerCell}>Name {sortIcon("schemeName")}</th>
              <th className={headerCell}>Code {sortIcon("schemeCode")}</th>
              <th className={headerCell}>Type {sortIcon("schemeType")}</th>
              <th className={headerCell}>
                Installments {sortIcon("installmentMonths")}
              </th>
              <th className={headerCell}>Benefit Months</th>
              <th className={headerCell}>Maturity (Months)</th>
              <th className={headerCell}>Min Amount</th>
              <th className={headerCell}>Status</th>
              <th className={`${headerCell} sticky right-0 top-0 z-50 w-[82px] min-w-[82px] border-l border-violet-200 bg-[#ddd5f7] text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.45)] sm:w-[104px] sm:min-w-[104px]`}><span className="sm:hidden">Act</span><span className="hidden sm:inline">Action</span></th>
            </tr>
          </thead>

          <tbody className="bg-white text-gray-700">
            <AnimatePresence initial={false}>
              {isFetching && !isSuccess ? (
                <SkeletonRows cols={10} rows={6} />
              ) : rows.length ? (
                rows.map((scheme, idx) => {
                  const rowIndex = startIndex + idx + 1;
                  const isAmount = scheme.schemeType === "AMOUNT";
                  const isActive = scheme.isActive;

                  return (
                    <motion.tr
                      key={scheme._id || idx}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.2, delay: idx * 0.02 }}
                      className="group border-t transition hover:bg-[#f4f0ff]"
                    >
                      <td className={dataCell}>{rowIndex}</td>

                      <td className={`${dataCell} font-medium`}>
                        {scheme.schemeName}
                      </td>

                      <td className={dataCell}>{scheme.schemeCode || "-"}</td>

                      <td className={dataCell}>
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] ${
                            isAmount
                              ? "bg-amber-50 text-amber-700"
                              : "bg-sky-50 text-sky-700"
                          }`}
                        >
                          {toTitle(scheme.schemeType)}
                        </span>
                      </td>

                      <td className={dataCell}>
                        {scheme.installmentMonths ?? "-"}
                      </td>

                      <td className={dataCell}>
                        {scheme.benefitsInMonths ?? "-"}
                      </td>

                      <td className={dataCell}>
                        {scheme.schemeMaturityInMonths ?? "-"}
                      </td>

                      <td className={dataCell}>
                        <span>
                          ₹{" "}
                          {scheme.schemeAmountPerMonth?.toLocaleString?.() || 0}{" "}
                          / month
                        </span>
                      </td>

                      <td className={dataCell}>
                        <button
                          type="button"
                          onClick={() => handleToggleActive(scheme)}
                          disabled={toggling}
                          className={`px-2 py-0.5 rounded-full text-[11px] border ${
                            isActive
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-slate-100 text-slate-600 border-slate-200"
                          }`}
                        >
                          {isActive ? "Active" : "Inactive"}
                        </button>
                      </td>

                      <td
                        className={`${dataCell} sticky right-0 z-10 w-[82px] min-w-[82px] whitespace-nowrap border-l border-violet-100 bg-violet-50 text-center shadow-[-6px_0_10px_-10px_rgba(15,23,42,0.35)] transition group-hover:bg-violet-100 sm:w-[104px] sm:min-w-[104px]`}
                      >
                        {/* <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex items-center justify-center text-[#9481ce] text-[11px] p-1.5 rounded-full hover:bg-[#9481ce]/15"
                          title="Edit"
                          onClick={() => navigate(`/gold-schemes/edit/${scheme._id}`)}
                        >
                          <FaPencilAlt />
                        </motion.button> */}

                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="inline-flex items-center justify-center text-rose-500 text-[11px] p-1.5 rounded-full hover:bg-rose-50 disabled:opacity-40 focus:outline-none focus:ring-2 focus:ring-rose-200"
                          title="Delete"
                          disabled={deleting}
                          data-delete-btn={String(scheme._id)}
                          onClick={(e) => {
                            if (canDelete(user)) {
                              openDeleteConfirm(scheme, e);
                            } else {
                              toast.error(
                                "You don't have permission to delete",
                              );
                            }
                          }}
                        >
                          <FaTrashAlt />
                        </motion.button>
                      </td>
                    </motion.tr>
                  );
                })
              ) : (
                <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <td
                    colSpan={10}
                    className="px-4 py-8 text-center text-xs text-slate-500"
                  >
                    No gold schemes found. Try changing filters or add a new
                    scheme.
                  </td>
                </motion.tr>
              )}
            </AnimatePresence>
          </tbody>
        </table>
      </div>

      {/* footer pagination */}
      <div className="flex shrink-0 items-center justify-between gap-2 rounded-b-2xl border-t bg-slate-50 px-2 py-1.5 text-[10px] sm:gap-3 sm:px-4 sm:py-2 sm:text-[11px]">
        <div className="hidden items-center gap-2 sm:flex">
          <span className="text-slate-600">
            Showing{" "}
            <span className="font-semibold">
              {rows.length ? startIndex + 1 : 0}
            </span>{" "}
            to{" "}
            <span className="font-semibold">
              {rows.length ? startIndex + rows.length : 0}
            </span>{" "}
            of <span className="font-semibold">{total}</span> schemes
          </span>
        </div>

        <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-start sm:gap-3">
          <div className="flex shrink-0 items-center gap-1">
            <span className="hidden text-slate-500 sm:inline">Rows:</span>
            <Select
              value={{ value: limit, label: String(limit) }}
              options={[5, 10, 20, 50].map((number) => ({
                value: number,
                label: String(number),
              }))}
              onChange={(option) => {
                setLimit(Number(option?.value) || 10);
                setPage(1);
              }}
              isDisabled={isFetching}
              isSearchable={false}
              menuPlacement="top"
              menuPosition="fixed"
              menuPortalTarget={document.body}
              menuShouldScrollIntoView={false}
              styles={paginationSelectStyles}
              className="w-[54px]"
              aria-label="Rows per page"
            />
            <span className="text-[9px] text-slate-400 sm:hidden">/ page</span>
          </div>

          <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:w-auto sm:flex-none sm:justify-start sm:gap-1">
            <button
              type="button"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Previous page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &lsaquo;
              </span>
              <span className="hidden sm:inline">Prev</span>
            </button>
            <span className="text-slate-600">
              <span className="flex items-center gap-1 sm:hidden">
                <Select
                  value={{ value: page, label: String(page) }}
                  options={Array.from({ length: totalPages }, (_, index) => ({
                    value: index + 1,
                    label: String(index + 1),
                  }))}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[52px]"
                  aria-label="Select page number"
                />
                <span>/ {totalPages}</span>
              </span>
              <span className="hidden items-center gap-1 sm:flex">
                <span>Page</span>
                <Select
                  value={{ value: page, label: String(page) }}
                  options={Array.from({ length: Math.max(1, totalPages) }, (_, index) => ({ value: index + 1, label: String(index + 1) }))}
                  onChange={(option) => setPage(Number(option?.value) || 1)}
                  isDisabled={isFetching}
                  isSearchable={false}
                  menuPlacement="top"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                  menuShouldScrollIntoView={false}
                  styles={paginationSelectStyles}
                  className="w-[58px]"
                  aria-label="Select page number"
                />
                <span>of {Math.max(1, totalPages)}</span>
              </span>
            </span>
            <button
              type="button"
              disabled={page >= totalPages || isFetching}
              onClick={() =>
                setPage((p) => (totalPages ? Math.min(totalPages, p + 1) : p))
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-base hover:bg-slate-100 disabled:opacity-40 sm:h-auto sm:w-auto sm:rounded-md sm:px-2 sm:py-1 sm:text-[11px]"
              aria-label="Next page"
            >
              <span className="sm:hidden" aria-hidden="true">
                &rsaquo;
              </span>
              <span className="hidden sm:inline">Next</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filter modal */}
      <AnimatePresence>
        {isFilterOpen && (
          <motion.div
            className="fixed inset-0 z-[9998] bg-black/40 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col"
              initial={{ scale: 0.9, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.9, y: 20, opacity: 0 }}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div>
                  <h3 className="text-sm font-semibold text-slate-800">
                    Advanced filters
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    Filter schemes by type, status and branch.
                  </p>
                </div>
                <button
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setIsFilterOpen(false)}
                >
                  <FaTimesCircle />
                </button>
              </div>

              <div className="p-4 space-y-4 text-[11px] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-slate-600 mb-1">
                      Scheme Type
                    </label>
                    <select
                      value={schemeTypeFilter}
                      onChange={(e) => setSchemeTypeFilter(e.target.value)}
                      className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    >
                      <option value="">All</option>
                      <option value="AMOUNT">Amount</option>
                      <option value="GRAM">Gram</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-slate-600 mb-1">Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-2 py-1.5 rounded-lg border border-slate-200 text-[11px] focus:outline-none focus:ring-1 focus:ring-violet-400"
                    >
                      <option value="true">Active</option>
                      <option value="false">Inactive</option>
                      <option value="">All</option>
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-slate-600 mb-1">Branch</label>
                    <Select
                      styles={selectStyles}
                      value={branchValue}
                      options={branchOptions}
                      isClearable
                      onChange={(opt) => setBranchFilter(opt?.value || "")}
                      menuPortalTarget={document.body}
                      menuPlacement="auto"
                    />
                  </div>
                </div>
              </div>

              <div className="px-4 py-3 border-t bg-slate-50 flex items-center justify-between">
                <button
                  type="button"
                  onClick={resetFilters}
                  className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-800"
                >
                  <FiRotateCcw />
                  Reset
                </button>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsFilterOpen(false)}
                    className="px-3 py-1.5 rounded-md border border-slate-200 text-[11px] text-slate-700 bg-white hover:bg-slate-100"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={confirm.open}
        title="Delete this scheme?"
        description={`Are you sure you want to delete scheme "${
          confirm.payload?.name || ""
        }"?`}
        confirmLabel="Yes, delete"
        cancelLabel="Cancel"
        onClose={closeConfirm}
        onCancel={closeConfirm}
        onConfirm={handleConfirm}
        loading={deleting}
      />
    </div>
  );
};

export default GoldSchemeList;
